import PropTypes from 'prop-types';
import React from 'react';
import {
  Box, Button, Card, Divider, IconButton, Tooltip, Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import trLocale from 'date-fns/locale/tr';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';

import { formatDate, getDefaultDate, toIsoDate } from './format';
import { getLevelColor } from './colors';

/**
 * Takvimde seçili gün okunmuyordu: tema `primary.contrastText`'i
 * `primary.main` ile aynı turuncuya ayarladığı için (modules/theme/palette.js)
 * turuncu dairenin üstüne turuncu rakam yazılıyordu.
 */
const TAKVIM_SX = {
  '& .MuiPickersDay-root.Mui-selected, & .MuiYearCalendar-button.Mui-selected, & .MuiMonthCalendar-button.Mui-selected':
    { color: '#fff' },
  '& .MuiPickersDay-root.Mui-selected:hover, & .MuiPickersDay-root.Mui-selected:focus, & .MuiPickersDay-root.Mui-selected.Mui-focusVisible':
    { color: '#fff' }
};

const ACIKLAMA =
  'Çağrı merkezi verisi her gece Walboard servisinden çekilip SARIS’e yazılır. ' +
  'Aktarım bir gün geriden çalışır: gün içinde çağrı kaydı akmaya devam ettiği ' +
  'için o günü çekmek günün kalanını kaçırırdı. Bu yüzden ekran dünün tarihiyle ' +
  'açılır.';

const gunEkle = (tarih, adet) => {
  const d = new Date(tarih);
  d.setDate(d.getDate() + adet);
  d.setHours(0, 0, 0, 0);
  return d;
};

/**
 * Tarih seçim çubuğu.
 *
 * Oklar günü tek tıkla ilerletip veriyi hemen getirir — günlük bir raporda en
 * sık yapılan hareket bu. Takvimden seçim ise Ara ile onaylanır; seçilen gün
 * ile ekrandaki gün ayrıldığında sağdaki blok bunu yazar, böylece kullanıcı
 * hangi günün verisine baktığını hiçbir zaman tahmin etmek zorunda kalmaz.
 */
const DateToolbar = ({ date, loadedDate, loading, onChange, onSearch }) => {
  const theme = useTheme();
  const uyari = getLevelColor(theme, 'warn');
  const bugun = new Date();
  bugun.setHours(0, 0, 0, 0);

  const secili = toIsoDate(date);
  const ileriKapali = !secili || secili >= toIsoDate(bugun);
  const uygulanmadi = Boolean(secili && loadedDate && secili !== loadedDate);

  const gunDegistir = (adet) => {
    const yeni = gunEkle(date || getDefaultDate(), adet);
    onChange(yeni);
    onSearch(yeni);
  };

  return (
    <Card
      sx={{
        mb: 2,
        px: 2,
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        flexWrap: 'wrap'
      }}
    >
      {/* ------------------------------------------------- gün seçimi */}
      <Tooltip title="Önceki gün">
        <span>
          <IconButton size="small" onClick={() => gunDegistir(-1)} disabled={loading}>
            <ChevronLeftIcon />
          </IconButton>
        </span>
      </Tooltip>

      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={trLocale}>
        <DatePicker
          label="Veri tarihi"
          format="dd.MM.yyyy"
          value={date}
          onChange={onChange}
          maxDate={bugun}
          slotProps={{
            textField: { size: 'small', sx: { width: 176 } },
            popper: { sx: TAKVIM_SX },
            mobilePaper: { sx: TAKVIM_SX }
          }}
        />
      </LocalizationProvider>

      <Tooltip title="Sonraki gün">
        <span>
          <IconButton
            size="small"
            onClick={() => gunDegistir(1)}
            disabled={loading || ileriKapali}
          >
            <ChevronRightIcon />
          </IconButton>
        </span>
      </Tooltip>

      <Button
        variant="contained"
        startIcon={<SearchIcon />}
        onClick={() => onSearch(date)}
        disabled={loading}
        sx={{ height: 40, px: 2.5 }}
      >
        Ara
      </Button>

      <Button
        size="small"
        disabled={loading || secili === toIsoDate(getDefaultDate())}
        onClick={() => {
          const dun = getDefaultDate();
          onChange(dun);
          onSearch(dun);
        }}
        sx={{ textTransform: 'none' }}
      >
        Düne dön
      </Button>

      <Box sx={{ flexGrow: 1 }} />

      {/* --------------------------------------- görüntülenen gün + açıklama */}
      <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <EventOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
        <Box>
          <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', lineHeight: 1.3 }}>
            Görüntülenen gün
          </Typography>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 500, lineHeight: 1.3 }}>
            {formatDate(loadedDate)}
          </Typography>
        </Box>

        {uygulanmadi && (
          <Box
            component="span"
            sx={{
              ml: 0.5,
              px: 1,
              py: '2px',
              borderRadius: 5,
              fontSize: '0.7rem',
              fontWeight: 500,
              color: uyari.text,
              backgroundColor: uyari.bg
            }}
          >
            Ara’ya basın
          </Box>
        )}

        <Tooltip title={ACIKLAMA} arrow placement="bottom-end">
          <InfoOutlinedIcon
            sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help', ml: 0.25 }}
          />
        </Tooltip>
      </Box>
    </Card>
  );
};

DateToolbar.propTypes = {
  date: PropTypes.instanceOf(Date),
  loadedDate: PropTypes.string,
  loading: PropTypes.bool,
  onChange: PropTypes.func,
  onSearch: PropTypes.func
};

export default DateToolbar;
